import { useState } from "react";

import type { MemberCustomerVo } from "@zhihaoscm/entity";
import { dashboardService } from "@/api/services/dashboardService";
import { formatThousandSeparator } from "@/utils/money";
import { formatNumber } from "@/utils/number";
import {
    DashboardDateSelect,
    type DashboardDateValue,
    DataWrap,
    toDashboardDateParams,
    useDashboardQuery,
    WidgetBody
} from "../dashboardWidgets";

/** 用户转化分析，复刻 Vue 的注册用户到会员用户转化漏斗。 */
export default function CustomerConvertAnalysis() {
    const [time, setTime] = useState<DashboardDateValue>(1);
    const { data, loading, error } = useDashboardQuery<Partial<MemberCustomerVo>>(
        () => dashboardService.getCustomerConvertAnalysis(toDashboardDateParams(time)),
        [time]
    );
    const conversionRate = formatThousandSeparator(data?.memberConvertRate || 0);
    const registerNum = formatNumber(data?.registerNum);
    const memberNum = formatNumber(data?.memberNum);

    return (
        <DataWrap
            title="用户转化分析"
            className="customer-convert-analysis"
            option={
                <DashboardDateSelect
                    value={time}
                    onChange={setTime}
                    options={["all", "month", "quarter", "year"]}
                    defaultActive="all"
                />
            }
        >
            <WidgetBody loading={loading} error={error}>
                <div className="customer-convert-main">
                    <svg width="310" height="360" viewBox="0 0 310 360" role="img" aria-label="用户转化分析漏斗">
                        <defs>
                            <linearGradient
                                id="customer-convert-bottom"
                                x1="157"
                                y1="182"
                                x2="157"
                                y2="357"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop stopColor="#fff" />
                                <stop offset="1" stopColor="#fcf7e5" />
                            </linearGradient>
                            <linearGradient
                                id="customer-convert-top"
                                x1="155"
                                y1="39"
                                x2="155"
                                y2="214"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop stopColor="#fff" />
                                <stop offset="1" stopColor="#edf1fb" />
                            </linearGradient>
                            <linearGradient
                                id="customer-convert-blue-line"
                                x1="157"
                                y1="217"
                                x2="157"
                                y2="157"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop offset=".43" stopColor="#385aa6" />
                                <stop offset="1" stopColor="#fff" stopOpacity="0" />
                            </linearGradient>
                            <linearGradient
                                id="customer-convert-yellow-line"
                                x1="158"
                                y1="360"
                                x2="158"
                                y2="298"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop offset=".43" stopColor="#ffe351" />
                                <stop offset="1" stopColor="#fff" stopOpacity="0" />
                            </linearGradient>
                            <linearGradient
                                id="customer-convert-top-line"
                                x1="156"
                                y1="77"
                                x2="156"
                                y2="-19"
                                gradientUnits="userSpaceOnUse"
                            >
                                <stop offset=".56" stopColor="#385aa6" />
                            </linearGradient>
                        </defs>
                        <path
                            d="M70.3 334.4 40.8 191c3 14.7 52.4 24.8 115.2 24.8S268.8 203.9 271.2 191l-26.9 143.4c-1.6 9.9-26.4 23.2-88.3 24.7-61.9 1.4-82.9-16.3-85.7-24.7Z"
                            fill="url(#customer-convert-bottom)"
                        />
                        <path
                            d="M0 38.6 1 42.5c7.4 14.3 54.8 33.3 153.5 33.3 99.6 0 148.8-20.1 155.7-34l.8-3.2c0 1-.3 2.1-.9 3.2l-38.9 149.4c-2.1 9.9-34.7 22.2-116.7 23.7-82.1 1.4-110.2-15.3-114-23.7L1 42.5c-.7-1.4-1-2.7-1-3.9Z"
                            fill="url(#customer-convert-top)"
                        />
                        <ellipse
                            cx="156"
                            cy="191"
                            rx="115"
                            ry="25"
                            fill="none"
                            stroke="url(#customer-convert-blue-line)"
                            strokeWidth="1.84"
                        />
                        <ellipse
                            cx="157"
                            cy="333"
                            rx="87"
                            ry="26"
                            fill="none"
                            stroke="url(#customer-convert-yellow-line)"
                            strokeWidth="1.84"
                        />
                        <ellipse
                            cx="155"
                            cy="39"
                            rx="154"
                            ry="38"
                            fill="none"
                            stroke="url(#customer-convert-top-line)"
                            strokeWidth="1.84"
                        />
                        <text x="156" y="34" textAnchor="middle" fill="#333" fontSize="18" fontWeight="500">
                            会员转化率
                        </text>
                        <text x="156" y="58" textAnchor="middle" fill="#333" fontSize="18" fontWeight="500">
                            {conversionRate}%
                        </text>
                        <text x="152" y="150" textAnchor="middle" fill="#385aa6" fontSize="12">
                            注册用户
                        </text>
                        <text x="156" y="178" textAnchor="middle" fill="#385aa6" fontSize="24" fontWeight="500">
                            {registerNum}人
                        </text>
                        <text x="152" y="292" textAnchor="middle" fill="#dba232" fontSize="12">
                            会员用户
                        </text>
                        <text x="156" y="321" textAnchor="middle" fill="#dba232" fontSize="24" fontWeight="500">
                            {memberNum}人
                        </text>
                    </svg>
                </div>
            </WidgetBody>
        </DataWrap>
    );
}
